import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PANEL_BASE, panelPath } from '../config/paths';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AppError } from '../api/request';
import AuthLayout from './AuthLayout';
import BrandLogo from './BrandLogo';

const PRO_MONTHLY = 20;
/** Yearly = pay 10 months, get 2 free → effective monthly. */
const PRO_YEARLY_EFFECTIVE_MONTHLY = Math.round(((PRO_MONTHLY * 10) / 12) * 100) / 100;

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') ?? '';
  const billing = (searchParams.get('billing') === 'yearly' ? 'yearly' : 'monthly') as 'monthly' | 'yearly';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isProIntent = plan === 'pro';
  const isEnterpriseIntent = plan === 'enterprise';
  const planLabel = isProIntent
    ? billing === 'yearly'
      ? t('auth.registerProYearly', { price: PRO_YEARLY_EFFECTIVE_MONTHLY })
      : t('auth.registerProMonthly', { price: PRO_MONTHLY })
    : isEnterpriseIntent
      ? t('auth.registerEnterprise')
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    setSubmitting(true);
    try {
      await register(email.trim(), password);
      if (isProIntent || isEnterpriseIntent) {
        const params = new URLSearchParams();
        if (plan) params.set('plan', plan);
        if (isProIntent && billing) params.set('billing', billing);
        navigate(panelPath('upgrade' + (params.toString() ? '?' + params.toString() : '')), { replace: true });
      } else {
        navigate(PANEL_BASE, { replace: true });
      }
    } catch (err) {
      setError(err instanceof AppError ? err.userMessage : t('auth.registerError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center mb-8">
        <BrandLogo className="h-14 w-14 rounded-xl mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('auth.signUpTo', { appName: t('common.appName') })}
        </h1>
      </div>

      {planLabel && (
        <div className="flex items-center gap-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 px-4 py-3 mb-6">
          <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
          <span className="text-sm font-medium text-primary-800 dark:text-primary-200">{planLabel}</span>
        </div>
      )}

      <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">{t('auth.registerTitle')}</h2>
      <form onSubmit={handleSubmit} className="space-y-4 mt-2">
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="register-email" className="sr-only">
            {t('common.email')}
          </label>
          <input
            id="register-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="input rounded-lg"
            placeholder={t('auth.emailPlaceholder')}
            required
          />
        </div>
        <div>
          <label htmlFor="register-password" className="sr-only">
            {t('auth.passwordHint')}
          </label>
          <input
            id="register-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="input rounded-lg"
            placeholder={t('auth.passwordHint')}
            required
          />
        </div>
        <div>
          <label htmlFor="register-confirm" className="sr-only">
            {t('auth.passwordConfirm')}
          </label>
          <input
            id="register-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className="input rounded-lg"
            placeholder={t('auth.passwordConfirm')}
            required
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary w-full py-2.5 font-medium rounded-lg"
        >
          {submitting ? t('auth.registering') : t('auth.register')}
        </button>
      </form>

      <p className="mt-5 text-center text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
        {t('auth.signUpAgreementBefore')}{' '}
        <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">
          {t('footer.legalPrivacy')}
        </Link>{' '}
        {t('auth.signUpAgreementAnd')}{' '}
        <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">
          {t('footer.legalTerms')}
        </Link>
        .
      </p>

      <p className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-600 dark:text-gray-400">
        {t('auth.hasAccount')}{' '}
        <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
          {t('auth.signInLink')}
        </Link>
      </p>
    </AuthLayout>
  );
}
